'use client';

import { useState, useEffect } from 'react';

import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { X, ArrowRight } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { getIntegrationBySlug } from '@/lib/integrations-data';

export function FloatingIntegrationBar() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const pathname = usePathname();
  const { t } = useTranslation();

  // Extract integration slug from pathname (e.g., /integrations/notion or /ar/integrations/notion -> notion)
  const pathSegments = pathname.split('/').filter(Boolean);
  const integrationsIndex = pathSegments.indexOf('integrations');
  const integrationSlug =
    integrationsIndex >= 0 ? pathSegments[integrationsIndex + 1] || '' : '';
  const integration = getIntegrationBySlug(integrationSlug, t.integrations);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000); // 3 seconds

    return () => clearTimeout(timer);
  }, []);

  if (isDismissed || !isVisible || !integration) return null;

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
      <div className="fixed bottom-6 left-1/2 z-50 animate-[slideUp_0.4s_ease-out_forwards]">
        <div className="flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-4 py-2.5 shadow-lg md:px-5 md:py-3">
          {/* Integration Icon */}
          {integration.logoUrl ? (
            <Image
              src={integration.logoUrl}
              alt={integration.name}
              width={20}
              height={20}
              className="h-5 w-5"
              unoptimized
            />
          ) : (
            <span className="text-xl">{integration.icon}</span>
          )}

          {/* Text */}
          <span className="text-sm text-neutral-700 md:text-base">
            {t.common['Want to train your AI on']}{' '}
            <span className="font-medium text-neutral-900">
              {integration.name}
            </span>
            ?
          </span>

          {/* Book a demo link */}
          <Link
            href="/demo"
            aria-label={t.common['Learn more about Open and book a demo']}
            className="group flex items-center gap-1 rounded-full bg-black px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
          >
            {t.common['Book a demo']}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>

          {/* Close button */}
          <button
            onClick={() => setIsDismissed(true)}
            className="ml-1 rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
            aria-label={t.common.Dismiss}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}
